import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { artifactClient, captureSolutionReviewsClient } from '../../../lib/connect/clients';
import { TextField } from '../PastPerformance/Fields';
import { itemKindLabel } from './SolutionItems';
const T = Testids.captureSolutionReview;
const fields = {
    approach: 'Approach',
    intended_outcomes: 'Intended outcomes',
    rationale: 'Rationale',
    constraints: 'Constraints',
    unresolved_issues: 'Unresolved issues',
    assumptions: 'Assumptions',
    proposed_approach: 'Proposed approach'
};
function readablePosition(review, value = review.currentPosition) {
    if (review.targetKind !== 'concept' || review.targetField !== 'assumptions') return value;
    try {
        const values = JSON.parse(value);
        return Array.isArray(values) ? values.join('\n') : '';
    } catch  {
        return '';
    }
}
function targetName(review, head) {
    if (review.targetKind === 'concept') return `Working concept · ${fields[review.targetField] ?? 'Narrative'}`;
    if (review.targetKind === 'component') {
        const component = head?.components.find((c)=>c.id === review.targetId);
        return `${component?.name ?? 'Former component'} · Proposed approach`;
    }
    const item = head?.items.find((i)=>i.id === review.targetId);
    const kind = itemKindLabel(item?.kind ?? 'former_item');
    return `${kind.charAt(0).toUpperCase() + kind.slice(1)} · ${item?.text ?? review.currentPosition}`;
}
function ReviewCard({ review, head, reload, activeId, onRefreshed, onReturn }) {
    const cache = useQueryClient();
    const [opened, setOpened] = useState(false);
    const [proposal, setProposal] = useState(()=>review.decisions[0]?.decision === 'deferred' ? readablePosition(review, review.decisions[0].proposedText) : '');
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [denied, setDenied] = useState(false);
    const [busy, setBusy] = useState(false);
    const lock = useRef(false);
    const retry = useRef();
    const comparison = useRef(null);
    useEffect(()=>{
        if (activeId === review.id) {
            setProposal('');
            setOpened(true);
        }
    }, [
        activeId,
        review.id
    ]);
    useEffect(()=>{
        if (opened && activeId === review.id) {
            comparison.current?.focus();
            comparison.current?.scrollIntoView({
                block: 'center'
            });
        }
    }, [
        opened,
        activeId,
        review.id
    ]);
    const last = review.decisions[0];
    const closed = last?.decision === 'adopted' || last?.decision === 'dismissed';
    const stale = review.baseRevisionId !== head?.id;
    const current = readablePosition(review);
    const proposedText = review.targetKind === 'concept' && review.targetField === 'assumptions' ? JSON.stringify(proposal.split('\n').map((line)=>line.trim()).filter(Boolean)) : proposal;
    const filename = useQuery({
        queryKey: [
            'solution-evidence-filename',
            review.artifactId,
            review.artifactVersion,
            review.sha256
        ],
        queryFn: async ()=>{
            const response = await artifactClient.getArtifact({
                artifactId: review.artifactId
            });
            const artifact = response.artifact;
            return artifact?.version === review.artifactVersion && artifact.checksumSha256 === review.sha256 ? artifact.filename : null;
        },
        retry: false,
        enabled: opened && !denied
    });
    async function act(decision) {
        if (lock.current) return;
        lock.current = true;
        setBusy(true);
        setError('');
        try {
            const args = {
                captureProjectId: review.captureProjectId,
                reviewId: review.id,
                expectedRevision: head?.revision ?? BigInt(0),
                decision,
                reason,
                proposedText: decision === 'adopted' || decision === 'deferred' ? proposedText : ''
            };
            const payload = JSON.stringify(args, (_, value)=>typeof value === 'bigint' ? String(value) : value);
            if (retry.current?.payload !== payload) retry.current = {
                payload,
                id: crypto.randomUUID()
            };
            await captureSolutionReviewsClient.decideCaptureSolutionReview({
                ...args,
                requestId: retry.current.id
            });
            retry.current = undefined;
            if (decision !== 'deferred') setProposal('');
            setReason('');
            await Promise.all([
                reload(),
                cache.invalidateQueries({
                    queryKey: [
                        'capture-solution',
                        review.captureProjectId
                    ]
                })
            ]);
        } catch (cause) {
            const code = ConnectError.from(cause).code;
            if (code === Code.PermissionDenied || code === Code.NotFound) {
                setDenied(true);
                setProposal('');
                setReason('');
                setError('This review is no longer available, or you do not have permission to change it.');
            } else if (code === Code.FailedPrecondition || code === Code.Aborted) {
                setProposal('');
                setError('The working solution changed. Reload the working version, explicitly refresh this review, then write a new proposal.');
                await cache.invalidateQueries({
                    queryKey: [
                        'capture-solution',
                        review.captureProjectId
                    ]
                });
            } else {
                setError('Could not confirm the decision. Retry the same action to safely check it, or edit your proposal for a new decision.');
            }
        } finally{
            lock.current = false;
            setBusy(false);
        }
    }
    async function refresh() {
        if (lock.current) return;
        lock.current = true;
        setBusy(true);
        setError('');
        setProposal('');
        setReason('');
        retry.current = undefined;
        try {
            const response = await captureSolutionReviewsClient.refreshCaptureSolutionReview({
                captureProjectId: review.captureProjectId,
                reviewId: review.id,
                expectedRevision: head?.revision ?? BigInt(0)
            });
            await reload();
            if (response.review) {
                setOpened(false);
                onRefreshed(response.review.id);
            }
        } catch (cause) {
            const code = ConnectError.from(cause).code;
            if (code === Code.PermissionDenied || code === Code.NotFound) setDenied(true);
            setError('Could not refresh this review. Reload the working version and check your access before retrying.');
        } finally{
            lock.current = false;
            setBusy(false);
        }
    }
    if (denied) return /*#__PURE__*/ _jsx("p", {
        role: "alert",
        children: error
    });
    return /*#__PURE__*/ _jsxs("article", {
        className: "space-y-2 rounded-md border p-3",
        "data-testid": T.row,
        "data-implication-id": review.id,
        "data-target-id": review.targetId,
        "data-target-field": review.targetField,
        "data-base-revision-id": review.baseRevisionId,
        "data-rfi-response-id": review.rfiResponseProjectId,
        "data-source-version-id": review.sourceVersionId,
        "data-artifact-id": review.artifactId,
        "data-artifact-version": review.artifactVersion,
        "data-sha256": review.sha256,
        "data-state": last?.decision ?? 'open',
        children: [
            /*#__PURE__*/ _jsx("h4", {
                className: "font-medium",
                children: targetName(review, head)
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-sm",
                children: [
                    last?.decision ?? 'Possible review',
                    stale && !closed ? ' · Earlier solution version' : ''
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": T.open,
                onClick: ()=>setOpened(true),
                children: "Review evidence"
            }),
            opened && /*#__PURE__*/ _jsxs("div", {
                ref: comparison,
                tabIndex: -1,
                className: "space-y-3",
                "data-testid": T.comparison,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm",
                        children: "Potential impact requires your judgment. No change is inferred or applied automatically."
                    }),
                    /*#__PURE__*/ _jsxs("h5", {
                        className: "font-medium",
                        children: [
                            "Position at review creation",
                            stale ? ' (earlier version)' : ''
                        ]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "whitespace-pre-wrap",
                        "data-testid": T.current,
                        children: current || 'No position recorded'
                    }),
                    /*#__PURE__*/ _jsx("h5", {
                        className: "font-medium",
                        children: "Cited new evidence"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": T.evidence,
                        className: "space-y-2 whitespace-pre-wrap",
                        children: [
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    filename.data ?? (filename.isLoading ? 'Loading document name…' : 'Document name unavailable'),
                                    ' ',
                                    "· ",
                                    review.sourceLabel,
                                    " · artifact version ",
                                    review.artifactVersion
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: review.evidenceState === 'text' ? review.evidenceText : review.evidenceState === 'non_text' ? 'Non-text document: no text preview is available.' : 'Exact evidence bytes are unavailable. No substitute or suggested text is shown.'
                            }),
                            review.impactState === 'no_changes' && /*#__PURE__*/ _jsx("p", {
                                children: "No source text changes were detected."
                            }),
                            review.impactState === 'unavailable' && /*#__PURE__*/ _jsx("p", {
                                children: "Automatic change comparison is unavailable. Review the cited evidence manually."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("details", {
                        children: [
                            /*#__PURE__*/ _jsx("summary", {
                                children: "Source lineage"
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "break-all text-xs",
                                children: [
                                    "Source ",
                                    review.sourceVersionId,
                                    " · Prior",
                                    ' ',
                                    review.priorSourceVersionId || 'Original source',
                                    " · Artifact ",
                                    review.artifactId,
                                    " · SHA256 ",
                                    review.sha256
                                ]
                            })
                        ]
                    }),
                    last && /*#__PURE__*/ _jsxs("p", {
                        "data-testid": T.decision,
                        "data-decision-id": last.id,
                        children: [
                            last.decision,
                            ": ",
                            last.reason || 'Left open for later review',
                            " · ",
                            last.actorUserId,
                            " ·",
                            ' ',
                            last.decidedAt?.toDate().toLocaleString()
                        ]
                    }),
                    !closed && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            stale ? /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        children: head ? 'The solution has changed. Refresh explicitly and re-author your proposal against the new snapshot.' : 'No working solution exists. Save a solution before refreshing this review.'
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": T.refresh,
                                        disabled: busy || !head,
                                        onClick: refresh,
                                        children: "Refresh against working version"
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(TextField, {
                                label: review.targetKind === 'concept' && review.targetField === 'assumptions' ? 'Proposed impact — write one assumption per line' : 'Proposed impact — write the revised position',
                                multiline: true,
                                testId: T.proposed,
                                value: proposal,
                                onChange: setProposal
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Decision reason",
                                hint: "Needed to apply or dismiss.",
                                multiline: true,
                                testId: T.reason,
                                value: reason,
                                onChange: setReason
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": T.adopt,
                                        disabled: busy || stale || !reason.trim() || !proposal.trim() || proposedText === review.currentPosition,
                                        onClick: ()=>act('adopted'),
                                        children: "Apply change"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": T.dismiss,
                                        disabled: busy || !reason.trim(),
                                        onClick: ()=>act('dismissed'),
                                        children: "Dismiss with reason"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": T.defer,
                                        disabled: busy,
                                        onClick: ()=>act('deferred'),
                                        children: "Defer"
                                    })
                                ]
                            })
                        ]
                    }),
                    busy && /*#__PURE__*/ _jsx("output", {
                        "data-loading": "true",
                        "data-testid": T.loading,
                        children: "Saving review…"
                    }),
                    error && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: error
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.back,
                        disabled: busy,
                        onClick: ()=>{
                            setOpened(false);
                            onReturn(review);
                        },
                        children: "Back to affected element"
                    })
                ]
            })
        ]
    });
}
export function SolutionEvidenceReviews({ captureProjectId, head, onReturn }) {
    const [activeId, setActiveId] = useState();
    const reviews = useQuery({
        queryKey: [
            'capture-solution-reviews',
            captureProjectId
        ],
        queryFn: async ()=>(await captureSolutionReviewsClient.listCaptureSolutionReviews({
                captureProjectId
            })).reviews,
        retry: false
    });
    if (reviews.isLoading) return /*#__PURE__*/ _jsx("output", {
        "data-loading": "true",
        "data-testid": T.loading,
        children: "Loading evidence reviews…"
    });
    if (reviews.isError) return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Evidence reviews could not be loaded. Check your access and try again."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>reviews.refetch(),
                children: "Retry evidence reviews"
            })
        ]
    });
    const groups = new Map();
    for (const review of reviews.data ?? [])groups.set(review.sourceVersionId, [
        ...groups.get(review.sourceVersionId) ?? [],
        review
    ]);
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        "aria-label": "New RFI and DRFP evidence",
        children: [
            /*#__PURE__*/ _jsxs("h3", {
                className: "font-semibold",
                "data-testid": T.banner,
                children: [
                    "New RFI / DRFP evidence ·",
                    ' ',
                    reviews.data?.filter((r)=>!r.decisions.some((d)=>d.decision === 'adopted' || d.decision === 'dismissed')).length ?? 0,
                    ' ',
                    "possible reviews"
                ]
            }),
            !head && /*#__PURE__*/ _jsx("p", {
                children: "No current solution. Source reviews cannot revise a solution until you create one."
            }),
            !groups.size && /*#__PURE__*/ _jsx("p", {
                children: "No evidence reviews available."
            }),
            [
                ...groups
            ].map(([id, group])=>/*#__PURE__*/ _jsxs("details", {
                    open: true,
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsxs("summary", {
                            children: [
                                group[0].sourceLabel,
                                " · ",
                                group.length,
                                " possible reviews"
                            ]
                        }),
                        group.map((review)=>/*#__PURE__*/ _jsx(ReviewCard, {
                                review: review,
                                head: head,
                                reload: reviews.refetch,
                                activeId: activeId,
                                onRefreshed: setActiveId,
                                onReturn: onReturn
                            }, review.id))
                    ]
                }, id))
        ]
    });
}
